import React, { useState } from "react";
import {
  List,
  useTable,
  EditButton,
  ShowButton,
  DeleteButton,
  CreateButton,
  FilterDropdown,
  DateField,
} from "@refinedev/antd";
import { useList, useCreate, HttpError } from "@refinedev/core";
import { 
  Table, 
  Space, 
  Tag, 
  Input, 
  Select, 
  Progress, 
  Button,
  Tooltip,
  message,
  Modal
} from "antd";
import {
  SearchOutlined,
  MailOutlined,
  HomeOutlined,
  EyeOutlined,
  CopyOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import type { ITaskList, IAdvert } from "../../interfaces";

const { Option } = Select;
const { confirm } = Modal;

export const TaskListList: React.FC = () => {
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const { tableProps, filters, setFilters } = useTable<ITaskList, HttpError>({
    resource: "tasklists",
    pagination: {
      pageSize: 20,
    },
    sorters: {
      initial: [
        {
          field: "last_activity",
          order: "desc",
        },
      ],
    },
  });

  // Adverts für Filter laden
  const { data: advertsData } = useList<IAdvert>({
    resource: "senders",
    pagination: {
      pageSize: 100,
    },
  });

  const adverts = advertsData?.data || [];

  // Token in Zwischenablage kopieren
  const copyToClipboard = (token: string, title: string) => {
    const mobileUrl = `${window.location.origin}/mobile/tasklist/${token}`;
    navigator.clipboard.writeText(mobileUrl).then(() => {
      setCopiedToken(token);
      message.success(`Mobile-Link für "${title}" kopiert!`);
      setTimeout(() => setCopiedToken(null), 3000);
    });
  };

  // Status-Tag rendern
  const renderStatus = (status: string) => {
    const statusConfig = {
      open: { color: "blue", icon: <ClockCircleOutlined />, text: "Offen" },
      in_progress: { color: "orange", icon: <SyncOutlined spin />, text: "In Arbeit" },
      completed: { color: "green", icon: <CheckCircleOutlined />, text: "Erledigt" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.open;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Fortschrittsanzeige
  const renderProgress = (statistics: ITaskList["statistics"]) => {
    if (!statistics) return "-";

    return (
      <Tooltip
        title={`${statistics.completed} von ${statistics.total} erledigt`}
      >
        <Progress
          percent={statistics.completionRate || 0}
          size="small"
          status={statistics.completionRate === 100 ? "success" : "active"}
          format={() => `${statistics.completed}/${statistics.total}`}
        />
      </Tooltip>
    );
  };

  const columns = [
    {
      title: "Titel",
      dataIndex: "title",
      key: "title",
      filterDropdown: (props: any) => (
        <FilterDropdown {...props}>
          <Input
            placeholder="Titel durchsuchen"
            prefix={<SearchOutlined />}
            value={filters?.find((f) => f.field === "title")?.value}
            onChange={(e) => {
              const newFilters = filters?.filter((f) => f.field !== "title") || [];
              if (e.target.value) {
                newFilters.push({
                  field: "title",
                  operator: "contains",
                  value: e.target.value,
                });
              }
              setFilters(newFilters);
            }}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Zugewiesen an",
      dataIndex: "assigned_email",
      key: "assigned_email",
      render: (email: string) => (
        <Space>
          <MailOutlined />
          {email}
        </Space>
      ),
      filterDropdown: (props: any) => (
        <FilterDropdown {...props}>
          <Input
            placeholder="E-Mail durchsuchen"
            prefix={<MailOutlined />}
            value={filters?.find((f) => f.field === "assigned_email")?.value}
            onChange={(e) => {
              const newFilters = filters?.filter((f) => f.field !== "assigned_email") || [];
              if (e.target.value) {
                newFilters.push({
                  field: "assigned_email",
                  operator: "contains",
                  value: e.target.value,
                });
              }
              setFilters(newFilters);
            }}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Objekt",
      dataIndex: "advert",
      key: "advert",
      render: (advertId: number) => {
        const advert = adverts.find(a => a.id === advertId);
        return advert ? (
          <Space>
            <HomeOutlined />
            {advert.title}
          </Space>
        ) : "-";
      },
      filterDropdown: (props: any) => (
        <FilterDropdown {...props}>
          <Select
            style={{ width: 200 }}
            placeholder="Objekt wählen"
            allowClear
            value={filters?.find((f) => f.field === "advert")?.value}
            onChange={(value) => {
              const newFilters = filters?.filter((f) => f.field !== "advert") || [];
              if (value) {
                newFilters.push({
                  field: "advert",
                  operator: "eq",
                  value: value,
                });
              }
              setFilters(newFilters);
            }}
          >
            {adverts.map((advert) => (
              <Option key={advert.id} value={advert.id}>
                {advert.title}
              </Option>
            ))}
          </Select>
        </FilterDropdown>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
      filterDropdown: (props: any) => (
        <FilterDropdown {...props}>
          <Select
            style={{ width: 150 }}
            placeholder="Status wählen"
            allowClear
            value={filters?.find((f) => f.field === "status")?.value}
            onChange={(value) => {
              const newFilters = filters?.filter((f) => f.field !== "status") || [];
              if (value) {
                newFilters.push({
                  field: "status",
                  operator: "eq",
                  value: value,
                });
              }
              setFilters(newFilters);
            }}
          >
            <Option value="open">Offen</Option>
            <Option value="in_progress">In Arbeit</Option>
            <Option value="completed">Erledigt</Option>
          </Select>
        </FilterDropdown>
      ),
    },
    {
      title: "Fortschritt",
      dataIndex: "statistics",
      key: "statistics",
      render: renderProgress,
    },
    {
      title: "Letzte Aktivität",
      dataIndex: "last_activity",
      key: "last_activity",
      render: (value: number) => value ? <DateField value={value * 1000} format="DD.MM.YYYY HH:mm" /> : "-",
      sorter: true,
    },
    {
      title: "Mobile-Link",
      dataIndex: "access_token",
      key: "access_token",
      render: (_, record) => {
        const token = record.access_token;
        const mobileUrl = `${window.location.origin}/?type=8847379250&token=${token}`;
        
        return (
          <Space>
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(mobileUrl);
                message.success("Mobile-Link kopiert!");
              }}
            >
              Mobile-Link kopieren
            </Button>
          </Space>
        );
      },
    },
    {
      title: "Aktionen",
      dataIndex: "actions",
      key: "actions",
      fixed: "right",
      render: (_: any, record: ITaskList) => (
        <Space>
          <ShowButton 
            hideText 
            size="small" 
            recordItemId={record.id} 
            title="Details anzeigen"
          />
          <EditButton 
            hideText 
            size="small" 
            recordItemId={record.id}
            title="Bearbeiten" 
          />
          <DeleteButton 
            hideText 
            size="small" 
            recordItemId={record.id}
            title="Löschen"
          />
        </Space>
      ),
    },
  ];

  return (
    <List
      headerButtons={
        <CreateButton>Neue Aufgabenliste</CreateButton>
      }
      title="Aufgabenlisten"
    >
      <Table 
        {...tableProps} 
        columns={columns} 
        rowKey="id"
        scroll={{ x: 1200 }}
      />
    </List>
  );
};